// Why: slice-91.5 symmetric collision check needs the canonical route
// SSOT (TAB_METADATA). The import is top-level (ESM) so vitest/Node
// resolution works without a require() shim. No circular dep: tabMetadata
// has no need to know about legacyRedirects.
import { TAB_METADATA } from './tabMetadata';

/**
 * legacyRedirects — SSOT for retired SPA paths that bookmarks may still
 * point at. Introduced by slice 91 (audit GAP-MIGRATION-SAFETY-LEGACY)
 * to close the migration-safety hole called out in the §17 migration
 * plan: the redesign sweep killed 13 rail items (Today / Inbox / At
 * risk / Disputes / Projects / Clients / Curation / Trends / Engagement
 * on the mentor side; Submissions / Recommendations / Progression /
 * Mentee-home on the mentee side), but no router-level redirect existed
 * yet — every bookmark to a killed path fell through to the SPA catch-
 * all and rendered a "page not found"-shaped surface.
 *
 * Why slice 91.5 dropped '/submissions' from the mentee section
 * ------------------------------------------------------------
 * '/submissions' is now the live canonical mentor Submissions rail
 * destination per TAB_METADATA.submissions.route (slices 72/86). A
 * legacy redirect on this path would hijack every mentor click on the
 * Submissions rail and route them to '/mentee/in-review' — a real
 * regression. Mentee bookmarks to the legacy mentee '/submissions' now
 * resolve to the mentor stub; the trade-off was accepted in slice 91.5
 * to preserve mentor UX (the mentor canonical surface is more visited +
 * more recently shipped). The CI enforcement is the symmetric collision
 * check assertNoCanonicalRouteCollision() at the bottom of this file;
 * see §22.20 for the audit trail of skeptic findings.
 *
 * What this file owns
 * -------------------
 *   - The canonical legacy-path → new-path mapping (one row per audit
 *     `retiredPaths` entry).
 *   - The deprecation-banner copy carried with each row (so the banner
 *     primitive — `LegacyRedirectBanner.tsx` — has no per-path
 *     branching of its own).
 *   - The loop-safety invariant: every `newPath` MUST be a canonical
 *     (non-legacy) path. Violations throw at module load.
 *
 * SOLID / GRASP / DRY / KISS
 * --------------------------
 *   - SRP: this file owns the redirect table; the router wires it,
 *     the banner consumes it, the analytics sink reads it.
 *   - DRY: every consumer (main.tsx <Route> generator, banner copy,
 *     test loop-safety pin) reads from `LEGACY_REDIRECTS` — no
 *     parallel list, no inline switch.
 *   - GRASP (Information Expert): the table is the expert on every
 *     legacy → canonical mapping; consumers never re-derive.
 *   - KISS: a single `Record` literal; no class hierarchy; no factory.
 *   - Deterministic + idempotent: re-running produces the same set of
 *     redirects; the loop-safety check is pure.
 *
 * exactOptionalPropertyTypes
 * --------------------------
 * `deprecationBannerText` is declared `?:` and the runtime never
 * passes `undefined` explicitly — consumers fall back to a generic
 * copy if a row omits it.
 *
 * Cross-references
 * ----------------
 *   - docs/redesign/mentorship-platform-redesign-2026-06-03.md
 *     §22.20 (this slice), §17 (migration plan), §17.5 / §17.9 /
 *     §17.10 (the kill-list phases this slice front-runs)
 *   - docs/redesign/plan-completeness-audit-2026-06-03.md
 *     (GAP-MIGRATION-SAFETY-LEGACY)
 *   - src/main.tsx (consumer — registers one <Route> per row before
 *     the SPA catch-all)
 *   - src/core/navigation/LegacyRedirect.tsx (consumer — the
 *     <Navigate replace> wrapper that sets the sessionStorage flag)
 *   - src/core/navigation/LegacyRedirectBanner.tsx (consumer — the
 *     informational top-of-viewport notice)
 */

// ── Row shape ──────────────────────────────────────────────────────────
//
// `legacyPath` is the SPA pathname the router matches; `newPath` is the
// destination <Navigate replace> sends the browser to. The optional
// `deprecationBannerText` is the per-row override of the generic banner
// copy — present iff the rename is non-obvious from the path itself.

export type LegacyRedirectRow = {
    /** The retired SPA pathname (e.g. `/today`, `/at-risk`). */
    legacyPath: string;
    /** The canonical destination. MUST NOT itself be a legacy path. */
    newPath: string;
    /**
     * Optional banner copy override. When omitted the banner primitive
     * falls back to a generic `"{legacyPath} → {newPath}"` template.
     * Per-row copy is preferred when the rename is non-obvious
     * (e.g. `/recommendations → /mentee/coaching` carries a vocabulary
     * shift the path alone doesn't convey).
     */
    deprecationBannerText?: string;
};

// ── LEGACY_REDIRECTS — the single source of truth ──────────────────────
//
// One row per audit `retiredPaths` entry. Cited inline against the
// `mentorship-platform-redesign-2026-06-03.md` §1.1 / §1.2 kill-list
// lines so a future copy-rename or path-rename has the audit row in
// arm's reach.
//
// Ordering is the audit's `retiredPaths` order (mentor surfaces first,
// then mentee surfaces). The router consumer (`main.tsx`) iterates in
// `Object.values` order; react-router matches the first <Route> whose
// path === pathname, so ordering here doesn't affect routing
// correctness — but it does make the SSOT diff against the audit
// trivial.

export const LEGACY_REDIRECTS = {
    // ── Mentor surfaces (audit §1.1 kill-list) ────────────────────────
    '/today': {
        legacyPath: '/today',
        newPath: '/',
        deprecationBannerText:
            "Today moved to Home. Your daily prep now sits in Inbox > Now on the home route.",
    },
    '/inbox': {
        legacyPath: '/inbox',
        newPath: '/',
        deprecationBannerText:
            "Needs Attention was merged into Home. The ranked stream is now Inbox > Now.",
    },
    '/at-risk': {
        legacyPath: '/at-risk',
        newPath: '/mentorship',
        deprecationBannerText:
            "At risk is now a saved filter under Mentees. Open Mentorship to find it.",
    },
    '/disputes': {
        legacyPath: '/disputes',
        newPath: '/mentorship',
        deprecationBannerText:
            "Disputes/Concerns became a saved filter under Mentees in Mentorship.",
    },
    '/projects': {
        legacyPath: '/projects',
        newPath: '/mentorship/:menteeId',
        deprecationBannerText:
            "Projects now sit inside each mentee's detail workspace under Mentorship.",
    },
    '/clients': {
        legacyPath: '/clients',
        newPath: '/mentorship',
        deprecationBannerText:
            "Clients was renamed Mentees. You'll find them in Mentorship.",
    },
    '/curation': {
        legacyPath: '/curation',
        newPath: '/insights',
        deprecationBannerText:
            "Curation moved into Insights > My library.",
    },
    '/trends': {
        legacyPath: '/trends',
        newPath: '/insights',
        deprecationBannerText:
            "Trends is now a tab inside the consolidated Insights rail item.",
    },
    '/engagement': {
        legacyPath: '/engagement',
        newPath: '/insights',
        deprecationBannerText:
            "Engagement merged into the Insights analytics group.",
    },
    // ── Mentee surfaces (audit §1.2 kill-list) ────────────────────────
    // Note on '/submissions' absence — see file-header note above + the
    // CI enforcement at `assertNoCanonicalRouteCollision()` below.
    '/recommendations': {
        legacyPath: '/recommendations',
        newPath: '/mentee/coaching',
        deprecationBannerText:
            "Recommendations was renamed Coaching to match your mentor's vocabulary.",
    },
    '/progression': {
        legacyPath: '/progression',
        newPath: '/mentee/progress',
        deprecationBannerText:
            "Progression became Progress > Trajectory and picked up Meetings as a sub-item.",
    },
    '/mentee-home': {
        legacyPath: '/mentee-home',
        newPath: '/mentee/today',
        deprecationBannerText:
            "Mentee Home was renamed Today (mirrors your mentor's Today surface).",
    },
} as const satisfies Readonly<Record<string, LegacyRedirectRow>>;

/** Convenience type — the legacy path string-literal union. */
export type LegacyPath = keyof typeof LEGACY_REDIRECTS;

// ── Loop-safety invariant (deterministic + idempotent) ─────────────────
//
// Every `newPath` MUST be a canonical (non-legacy) path. A row whose
// `newPath` is also a `legacyPath` key would create a redirect cycle —
// the browser would Navigate(replace) infinitely. We assert this at
// module load so the failure surfaces at import-time (and in CI on a
// fresh checkout), not at runtime in a user's browser.
//
// The check is pure: same `LEGACY_REDIRECTS` ⇒ same outcome, every
// invocation. Re-running the module on hot-reload re-runs the check
// idempotently (no side effect besides the throw on violation).
//
// Why a runtime check when the type system could enforce it? Because
// `newPath` values include parameterised paths (`/mentorship/:menteeId`)
// that don't appear as a key, AND because TypeScript's structural type
// system can't easily express "every value must not be in this set of
// keys" without devolving into mapped-type gymnastics that make the
// audit SSOT unreadable. The runtime check is one screen, one for-loop,
// and the failure mode is a clear `Error` with the offending row.

function assertNoRedirectCycles(): void {
    const legacyKeys = new Set<string>(Object.keys(LEGACY_REDIRECTS));
    for (const row of Object.values(LEGACY_REDIRECTS) as ReadonlyArray<LegacyRedirectRow>) {
        if (legacyKeys.has(row.newPath)) {
            throw new Error(
                `legacyRedirects: ${row.legacyPath} → ${row.newPath} would loop — `
                + `newPath is itself a legacy path. Fix the SSOT.`,
            );
        }
    }
}

// Slice 91.5 (skeptic remediation): assert that no legacyPath collides
// with a currently-canonical TAB_METADATA route. The original cycle
// check only validated newPath. Skeptics caught that '/submissions' was
// BOTH a legacy redirect AND the live canonical mentor Submissions rail
// destination — every mentor click on Submissions was being hijacked to
// '/mentee/in-review'. This symmetric check fails at module load if any
// legacyPath ever shadows a canonical TAB_METADATA route, preventing
// the regression from re-occurring as new rail routes are added.
//
// The check imports TAB_METADATA's route values lazily (inside the
// function) so the module-load order between legacyRedirects.ts and
// tabMetadata.ts doesn't matter.
function assertNoCanonicalRouteCollision(): void {
    const canonicalRoutes = new Set<string>();
    for (const meta of Object.values(TAB_METADATA)) {
        if (meta && typeof meta === 'object' && 'route' in meta && typeof meta.route === 'string') {
            canonicalRoutes.add(meta.route);
        }
    }
    for (const legacyPath of Object.keys(LEGACY_REDIRECTS)) {
        if (canonicalRoutes.has(legacyPath)) {
            throw new Error(
                `legacyRedirects: ${legacyPath} collides with a canonical `
                + `TAB_METADATA route. A click on the rail item with route `
                + `${legacyPath} would be hijacked by the legacy redirect. `
                + `Remove the legacy row or move the canonical route.`,
            );
        }
    }
}

assertNoRedirectCycles();
assertNoCanonicalRouteCollision();

// ── sessionStorage flag key (shared with banner consumer) ──────────────
//
// The <LegacyRedirect> wrapper writes this key on mount; the banner
// reads + clears it. Single-show per session: cleared on dismiss OR on
// the next route change AFTER the banner has mounted.
//
// Versioned (`v1.`) so a future copy-overhaul can bump the key and
// avoid showing stale banners to a returning user whose session
// pre-dates the new wording.
export const LEGACY_REDIRECT_FLAG_KEY = 'v1.legacy-redirect.banner';

// ── Banner copy resolver ───────────────────────────────────────────────
//
// SRP: the SSOT owns copy resolution; the banner primitive does not
// branch on the row. The generic fallback exists so a future row added
// without a deprecationBannerText still renders a sensible notice.

export function resolveBannerText(row: LegacyRedirectRow): string {
    if (row.deprecationBannerText) return row.deprecationBannerText;
    return `You followed an old bookmark. We renamed ${row.legacyPath} → `
        + `${row.newPath} when we simplified the navigation.`;
}
